import type { IconsType } from "@web-app/components";

import type { DetailsType, OrderFormField } from "./types";

export const DETAILS_TYPE_LABELS: Record<DetailsType, string> = {
  dropoff: "Drop-off details",
  order: "Order details",
  pickup: "Pickup details",
};

export const DETAILS_TYPES: DetailsType[] = ["pickup", "dropoff", "order"];

type SystemFieldDef = {
  colSpan: 1 | 2;
  detailsType: DetailsType;
  fieldKey: string;
  label: string;
};

const pickupSystemFields: SystemFieldDef[] = [
  {
    colSpan: 1,
    detailsType: "pickup",
    fieldKey: "systemField_fullName",
    label: "Full Name",
  },
  {
    colSpan: 1,
    detailsType: "pickup",
    fieldKey: "systemField_phone",
    label: "Phone Number",
  },
  {
    colSpan: 2,
    detailsType: "pickup",
    fieldKey: "systemField_address",
    label: "Address",
  },
  {
    colSpan: 2,
    detailsType: "pickup",
    fieldKey: "systemField_pickupAt",
    label: "Pickup At",
  },
  {
    colSpan: 1,
    detailsType: "pickup",
    fieldKey: "systemField_businessName",
    label: "Business Name",
  },
  {
    colSpan: 1,
    detailsType: "pickup",
    fieldKey: "systemField_serviceTime",
    label: "Service Time (minutes)",
  },
  {
    colSpan: 2,
    detailsType: "pickup",
    fieldKey: "systemField_orderRequirements",
    label: "Order Requirements",
  },
  {
    colSpan: 2,
    detailsType: "pickup",
    fieldKey: "systemField_note",
    label: "Note",
  },
  {
    colSpan: 2,
    detailsType: "pickup",
    fieldKey: "systemField_saveDetails",
    label: "Save Details to Address Book",
  },
];

const dropoffSystemFields: SystemFieldDef[] = [
  {
    colSpan: 1,
    detailsType: "dropoff",
    fieldKey: "systemField_fullName",
    label: "Full Name",
  },
  {
    colSpan: 1,
    detailsType: "dropoff",
    fieldKey: "systemField_phone",
    label: "Phone Number",
  },
  {
    colSpan: 2,
    detailsType: "dropoff",
    fieldKey: "systemField_address",
    label: "Address",
  },
  {
    colSpan: 2,
    detailsType: "dropoff",
    fieldKey: "systemField_dropoffAt",
    label: "Drop-off At",
  },
  {
    colSpan: 1,
    detailsType: "dropoff",
    fieldKey: "systemField_businessName",
    label: "Business Name",
  },
  {
    colSpan: 1,
    detailsType: "dropoff",
    fieldKey: "systemField_serviceTime",
    label: "Service Time (minutes)",
  },
  {
    colSpan: 2,
    detailsType: "dropoff",
    fieldKey: "systemField_orderRequirements",
    label: "Order Requirements",
  },
  {
    colSpan: 2,
    detailsType: "dropoff",
    fieldKey: "systemField_note",
    label: "Note",
  },
  {
    colSpan: 2,
    detailsType: "dropoff",
    fieldKey: "systemField_saveDetails",
    label: "Save Details to Address Book",
  },
];

const orderSystemFields: SystemFieldDef[] = [
  {
    colSpan: 2,
    detailsType: "order",
    fieldKey: "systemField_description",
    label: "Description",
  },
  {
    colSpan: 2,
    detailsType: "order",
    fieldKey: "systemField_timezone",
    label: "Timezone",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_externalId",
    label: "External Reference ID",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_vehicleType",
    label: "Mode of Transportation",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_value",
    label: "Value",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_tip",
    label: "Tip",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_itemsCount",
    label: "Item Count",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_length",
    label: "Length (cm)",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_width",
    label: "Width (cm)",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_height",
    label: "Height (cm)",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_weight",
    label: "Weight (gm)",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_priority",
    label: "Priority",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_disableSMS",
    label: "SMS",
  },
  {
    colSpan: 1,
    detailsType: "order",
    fieldKey: "systemField_itemChecklist",
    label: "Item Checklist",
  },
  {
    colSpan: 2,
    detailsType: "order",
    fieldKey: "systemField_connectedWorkspaceId",
    label: "Connected Workspace Options",
  },
];

export const ALL_SYSTEM_FIELDS: Record<DetailsType, SystemFieldDef[]> = {
  dropoff: dropoffSystemFields,
  order: orderSystemFields,
  pickup: pickupSystemFields,
};

export function getAvailableSystemFields(
  detailsType: DetailsType,
  currentFields: OrderFormField[],
): SystemFieldDef[] {
  const inUse = new Set(
    currentFields
      .filter((f) => f.detailsType === detailsType)
      .map((f) => f.fieldKey),
  );
  return ALL_SYSTEM_FIELDS[detailsType].filter((f) => !inUse.has(f.fieldKey));
}

const SYSTEM_FIELD_LABELS: Record<string, string> = Object.fromEntries(
  [...pickupSystemFields, ...dropoffSystemFields, ...orderSystemFields].map(
    (f) => [f.fieldKey, f.label],
  ),
);

export function getSystemFieldLabel(fieldKey: string): string | undefined {
  return SYSTEM_FIELD_LABELS[fieldKey];
}

export type PreviewType =
  | "checkbox"
  | "checkboxGroup"
  | "currency"
  | "date"
  | "number"
  | "phone"
  | "radio"
  | "select"
  | "text"
  | "textarea";

export type FieldPreviewConfig = {
  checkboxLabels?: string[];
  icon?: IconsType;
  placeholder?: string;
  prefix?: string;
  radioLabels?: string[];
  type: PreviewType;
};

const FIELD_PREVIEW: Record<string, FieldPreviewConfig> = {
  systemField_address: {
    icon: "locationDot",
    placeholder: "Enter location",
    type: "text",
  },
  systemField_businessName: {
    placeholder: "Enter business name",
    type: "text",
  },
  systemField_connectedWorkspaceId: {
    radioLabels: ["Create on behalf of workspace", "Send to workspace"],
    type: "radio",
  },
  systemField_description: {
    placeholder: "Enter order description",
    type: "textarea",
  },
  systemField_disableSMS: {
    checkboxLabels: ["Disable SMS notifications"],
    type: "checkbox",
  },
  systemField_dropoffAt: {
    icon: "fasCalendarDays",
    placeholder: "Select drop-off date/time",
    type: "date",
  },
  systemField_externalId: { placeholder: "123", type: "text" },
  systemField_fullName: {
    icon: "user",
    placeholder: "Enter full name",
    type: "text",
  },
  systemField_height: { placeholder: "0", type: "number" },
  systemField_itemChecklist: { checkboxLabels: ["Add Item"], type: "checkbox" },
  systemField_itemsCount: { placeholder: "0", type: "number" },
  systemField_length: { placeholder: "0", type: "number" },
  systemField_note: { placeholder: "Enter note", type: "textarea" },
  systemField_orderRequirements: {
    checkboxLabels: [
      "Signature required",
      "Photo required",
      "Notes required",
      "Item checklist required",
    ],
    type: "checkboxGroup",
  },
  systemField_phone: {
    placeholder: "Enter phone number",
    prefix: "US +1",
    type: "phone",
  },
  systemField_pickupAt: {
    icon: "fasCalendarDays",
    placeholder: "Select pickup date/time",
    type: "date",
  },
  systemField_priority: { checkboxLabels: ["High Priority"], type: "checkbox" },
  systemField_saveDetails: {
    checkboxLabels: ["Save details to Address Book"],
    type: "checkbox",
  },
  systemField_serviceTime: {
    placeholder: "Enter service time",
    type: "number",
  },
  systemField_timezone: { placeholder: "Select Timezone", type: "select" },
  systemField_tip: { placeholder: "0", prefix: "USD", type: "currency" },
  systemField_value: { placeholder: "0", prefix: "USD", type: "currency" },
  systemField_vehicleType: { placeholder: "Select Vehicle", type: "select" },
  systemField_weight: { placeholder: "0", type: "number" },
  systemField_width: { placeholder: "0", type: "number" },
};

const DEFAULT_PREVIEW: FieldPreviewConfig = {
  placeholder: "Enter value",
  type: "text",
};

export function getFieldPreview(fieldKey: string): FieldPreviewConfig {
  return FIELD_PREVIEW[fieldKey] ?? DEFAULT_PREVIEW;
}

export function isSystemField(fieldKey: string): boolean {
  return fieldKey.startsWith("systemField_");
}

export function isCustomField(fieldKey: string): boolean {
  return fieldKey.startsWith("customField_");
}

export function extractCustomFieldId(fieldKey: string): string {
  return fieldKey.replace("customField_", "");
}
